import { useEffect, useState } from 'react';
import { stats, listarPendenciasReinf, listarTxSemLcto, certsAlerta, dre, faturamento12, listarLancamentos } from '../api';
import { KPI, PageHeader, Loading, Empty, Alert } from '../components/common';
import { fmtMoeda, fmtMoedaCompacta, competenciaLabel, mesAtual } from '../lib/format';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

export default function Dashboard({ clienteId }) {
  const [stats0, setStats] = useState(null);
  const [pendReinf, setPendReinf] = useState(0);
  const [pendFin, setPendFin] = useState(0);
  const [certs, setCerts] = useState([]);
  const [dreData, setDreData] = useState(null);
  const [fat, setFat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comp, setComp] = useState(mesAtual());

  // Mes de referencia = ultimo mes COM movimento (empresa nova ou cliente
  // parado mostrava tudo zerado no mes corrente e parecia sem dados).
  useEffect(() => {
    if (!clienteId) return;
    setComp(mesAtual());
    listarLancamentos(clienteId, { limit: 1 }).then(r => {
      const ult = (r?.items || [])[0];
      if (ult?.data_lcto) setComp(String(ult.data_lcto).slice(0, 7));
    }).catch(() => {});
  }, [clienteId]);

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    const [ano, mes] = comp.split('-');

    Promise.allSettled([
      stats(),
      listarPendenciasReinf(clienteId, { per_apur: comp }),
      listarTxSemLcto(clienteId),
      certsAlerta(),
      dre(clienteId, { ano, mes, tipo: 'mensal' }),
      faturamento12(clienteId, { ano, mes }),
    ]).then(([s, pr, pf, c, d, f]) => {
      if (s.status === 'fulfilled') setStats(s.value);
      if (pr.status === 'fulfilled') setPendReinf(pr.value?.count || 0);
      if (pf.status === 'fulfilled') setPendFin(pf.value?.count || 0);
      if (c.status === 'fulfilled') setCerts(c.value?.items || []);
      if (d.status === 'fulfilled') setDreData(d.value?.relatorio);
      if (f.status === 'fulfilled') setFat(f.value?.relatorio);
      setLoading(false);
    });
  }, [clienteId, comp]);

  if (!clienteId) return <Empty title="Nenhum cliente selecionado" sub="Use o seletor no topo." />;
  if (loading) return <Loading />;

  const certsCriticos = certs.filter(c => c.severidade === 'critico' || c.severidade === 'vencido');
  const totais = dreData?.totais || {};
  const fatKpis = fat?.kpis || {};

  // Série pro gráfico de barras
  const serieFat = (fat?.meses || []).map(m => ({
    mes: competenciaLabel(m.competencia),
    Total: m.total,
  }));

  return (
    <>
      <PageHeader
        title="Visão geral"
        sub={`Mês de referência: ${competenciaLabel(comp)}`}
      />
      <div style={{ margin: '-6px 0 14px' }}>
        <input type="month" value={comp} onChange={e => e.target.value && setComp(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 9, border: '1px solid var(--border-strong)',
            background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13 }} />
      </div>

      {certsCriticos.length > 0 && (
        <Alert tipo="danger">
          ⚠ {certsCriticos.length} certificado(s) digital(is) {certsCriticos[0].severidade === 'vencido' ? 'vencido(s)' : 'com vencimento crítico'} —
          ações de Reinf/SPED ficarão bloqueadas.
        </Alert>
      )}

      <div className="kpi-grid">
        <KPI label="Faturamento 12m"      value={fmtMoeda(fatKpis.total_periodo)} sub={`média ${fmtMoedaCompacta(fatKpis.media_mensal)}/mês`} />
        <KPI label="Lucro Líquido (mês)"  value={fmtMoeda(totais.lucro_liquido)} variant={totais.lucro_liquido >= 0 ? 'success' : 'danger'} sub="apurado pelo DRE" />
        <KPI label="Pendências Reinf"     value={pendReinf} variant={pendReinf > 0 ? 'warning' : ''} sub="notas Sieg sem match no Finance" />
        <KPI label="Pendências contábeis" value={pendFin}   variant={pendFin > 0 ? 'warning' : ''}   sub="tx Finance sem mapeamento" />
        {fatKpis.variacao_yoy_pct !== null && (
          <KPI
            label="Variação vs 12m anterior"
            value={`${fatKpis.variacao_yoy_pct >= 0 ? '+' : ''}${fatKpis.variacao_yoy_pct?.toFixed(1)}%`}
            variant={fatKpis.variacao_yoy_pct >= 0 ? 'success' : 'danger'}
            sub={`12m anterior: ${fmtMoeda(fatKpis.total_12m_anterior)}`}
          />
        )}
      </div>

      <div className="card mb-2">
        <div className="card__title">Faturamento 12 meses</div>
        {serieFat.length === 0 ? (
          <Empty title="Sem lançamentos no período" sub="Lance receitas pra ver evolução aqui." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={serieFat} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-strong)" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                     axisLine={{ stroke: 'var(--border-strong)' }}
                     tickLine={{ stroke: 'var(--border-strong)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                     tickFormatter={fmtMoedaCompacta}
                     axisLine={{ stroke: 'var(--border-strong)' }}
                     tickLine={{ stroke: 'var(--border-strong)' }} />
              <Tooltip
                formatter={(v) => fmtMoeda(v)}
                contentStyle={{
                  background: 'var(--void-3)',
                  border: '1px solid var(--gold-pale)',
                  borderRadius: 6,
                  color: 'var(--text)',
                  fontFamily: 'var(--font-data)',
                  fontSize: 12,
                }}
                cursor={{ fill: 'var(--gold-pale)' }}
              />
              <Bar dataKey="Total" fill="#C4956A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card">
        <div className="card__title">Métricas globais do sistema</div>
        {stats0 ? (
          <div className="flex" style={{ gap: 24, fontSize: 12 }}>
            <span>Plano referencial: <b>{stats0.plano_referencial}</b> contas</span>
            <span>Lançamentos: <b>{(stats0.lancamentos_por_status || []).reduce((s, x) => s + x.n, 0)}</b></span>
            <span>Certs ativos: <b>{(stats0.certificados_por_status || []).find(x => x.status === 'ativo')?.n || 0}</b></span>
          </div>
        ) : <Empty title="Sem dados" />}
      </div>
    </>
  );
}
